import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { Countdown } from "../components/Countdown";
import { AfconBand } from "../components/TrackAnchor";
import { FxStamp } from "../components/AsOf";

const usd = (n) =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : n >= 1e3 ? `$${Math.round(n).toLocaleString()}` : `$${Math.round(n || 0)}`;
const ksh = (n) => (n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : `KSh ${Math.round(n / 1e6).toLocaleString()}m`);
const num = (n) => Math.round(n || 0).toLocaleString();

export default function CourtRollout() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [showAll, setShowAll] = useState(false);

  useSeo({
    title: "The Court Rollout — courts per county before AFCON 2027",
    description:
      "How many five-a-side courts each Kenyan county gets before the AFCON PAMOJA 2027 kick-off on 19 June 2027, what each one costs at that county's own cost of living, and how many players, teams and jobs they carry.",
  });

  useEffect(() => {
    api.get("/court-rollout")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the court rollout. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="court-rollout-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="court-rollout-loading" />;

  const t = d.totals;
  const before = d.rows.filter((r) => r.before_afcon > 0);
  const rows = showAll ? d.rows : before;

  return (
    <div className="bg-black pb-24" data-testid="court-rollout-page">
      <section className="px-5 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">The court rollout</p>
            <h1 className="display-1 mt-5 max-w-5xl">
              {t.courts_before} Courts In The Ground Before AFCON
            </h1>
            <p className="body-copy mt-8 max-w-3xl">{d.standfirst}</p>
          </Reveal>

          <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,380px)_1fr]">
            <div className="border border-maasai/40 bg-[#0b0b0b] p-6">
              <Countdown target={d.kick_off_iso} testId="rollout-countdown" />
              <p className="mono mt-4 text-[0.58rem] uppercase tracking-[0.16em] text-ochre">
                Kick-off {d.afcon.kick_off} · {d.months_to_kick_off} months to build
              </p>
            </div>
            <div className="grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="rollout-headlines">
              {[
                ["Courts before kick-off", num(t.courts_before), `${t.pct_before}% of all ${t.courts_total}`],
                ["Cost of that wave", usd(t.capex_before_usd), ksh(t.capex_before_ksh)],
                ["Counties covered", `${t.counties_before} of ${t.counties}`, "At least one court each in the corridor"],
                ["Players a year", num(t.players_before), `${num(t.teams_before)} teams, ${num(t.jobs_before)} jobs`],
              ].map(([label, value, note], i) => (
                <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`rollout-headline-${i}`}>
                  <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                  <p className="mt-4 font-display text-2xl font-bold leading-none text-white md:text-3xl">{value}</p>
                  <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="rollout-capacity">
            <p className="overline text-ochre">What sets the number</p>
            <p className="mt-3 max-w-4xl text-sm leading-relaxed text-zinc-300">
              {d.capacity.note} That is {d.capacity.courts_per_month} courts a month across{" "}
              {d.capacity.months_available} usable months — {d.capacity.courts_deliverable} courts — so delivery
              capacity, not money, is what caps the pre-AFCON wave.
            </p>
            <FxStamp fx={d.fx} testId="rollout-fx-stamp" />
          </div>

          <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
            <h2 className="display-3">County By County</h2>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="rollout-toggle-all"
              className="mono border border-zinc-700 px-4 py-2 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300 hover:border-ochre hover:text-ochre"
            >
              {showAll ? "Show only the pre-AFCON wave" : `Show all ${t.counties} counties`}
            </button>
          </div>

          <div className="mt-6 overflow-x-auto border border-zinc-800">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["County", "Courts before AFCON", "After", "Cost per court", "Cost of the wave", "Players a year", "Teams"].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-300">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.county} className="border-b border-zinc-900" data-testid={`rollout-row-${r.county}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      {r.county}
                      {r.priority ? (
                        <span className="mono ml-2 text-[0.5rem] uppercase tracking-[0.16em] text-maasai">corridor</span>
                      ) : null}
                    </td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{r.before_afcon}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{r.after_afcon}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-300">
                      {usd(r.unit_usd)}
                      <span className="mt-1 block text-[0.5rem] uppercase tracking-[0.14em] text-zinc-600">
                        index x{r.cost_index}
                      </span>
                    </td>
                    <td className="mono px-4 py-3 text-xs text-white">{usd(r.capex_before_usd)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{num(r.players_before)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{num(r.teams_before)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#0b0b0b]" data-testid="rollout-totals-row">
                  <td className="px-4 py-4 text-sm font-bold uppercase text-white">All counties</td>
                  <td className="mono px-4 py-4 text-sm text-ochre">{t.courts_before}</td>
                  <td className="mono px-4 py-4 text-xs text-zinc-400">{t.courts_after}</td>
                  <td className="mono px-4 py-4 text-xs text-zinc-400">
                    {usd(t.unit_usd)} national
                  </td>
                  <td className="mono px-4 py-4 text-xs text-white">{usd(t.capex_before_usd)}</td>
                  <td className="mono px-4 py-4 text-xs text-zinc-400">{num(t.players_before)}</td>
                  <td className="mono px-4 py-4 text-xs text-zinc-400">{num(t.teams_before)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <AfconBand afcon={d.afcon} />

          <div className="mt-14 border border-white/10 bg-[#0b0b0b] p-7" data-testid="rollout-method">
            <p className="overline">How this was worked out</p>
            <p className="mt-4 max-w-4xl whitespace-pre-line text-sm leading-relaxed text-zinc-400">{d.method}</p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
              {d.sources.map((s, i) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`rollout-source-${i}`}
                  className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-400 hover:text-ochre"
                >
                  {s.publisher} <ExternalLink size={11} />
                </a>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/facilities" className="btn-primary" data-testid="rollout-grassroots-link">
              The grassroots ecosystem <ArrowRight size={14} />
            </Link>
            <Link to="/plans?track=national" className="btn-ghost" data-testid="rollout-steps-link">
              The grassroots steps, costed
            </Link>
            <Link to="/counties" className="btn-ghost" data-testid="rollout-counties-link">
              The county map
            </Link>
            <Link to="/sources" className="btn-ghost" data-testid="rollout-sources-link">
              Sources and Method
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
